// What a box is ALLOWED to be given — the `runners.mode` role vocabulary, the env
// var an operator pins it with, the two defaults (unpinned vs unadvertised) and
// the two capability predicates the assigner reads.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * The runner's PINNED role (#368) — exactly `apps/runner/src/runtime.ts`'s boot-time
 * `RunnerMode` (`worker` | `universal`), so every value a box heartbeats round-trips
 * unchanged. Advertised on every heartbeat (`runners.mode`). Text + Zod-at-read (D3
 * no-pgEnum stance, same as `kind`/`ownerKind`).
 *
 * `monitor`, `scout` and `setup` were DELETED: the runner removed those pinned boot modes
 * (#5497 for scout/setup; their executors run per assignment on a `universal` box), and
 * no stored row carries them (`runner-role-vocabulary.test.ts` states the counts). A
 * pinned `scout`/`setup` box is what the two predicates below were written against — it
 * claimed a coding task and silently stalled (2026-07-09) — and cannot exist any more.
 *
 * `runners.mode` defaults to `worker` in the COLUMN, which is a placeholder for a
 * row that has not been told anything yet, not a role a box chose — see
 * {@link UNADVERTISED_RUNNER_ROLE}, and note that it is a DIFFERENT value from the
 * role an unpinned box actually boots into ({@link UNPINNED_RUNNER_ROLE}).
 *
 * Every role runs autonomous coding; only `universal` runs the INTERACTIVE (scout/setup
 * ORIGIN) session ({@link runnerRoleCanConverse}). The claim asks through
 * `runnerRoleCanRunOrigin` (`../value-objects/task-envelope`), which asks the converse
 * question for an interactive origin — never assume a coding-capable box converses:
 * that is how a `worker` box claimed a planning session and ran the coding adapter on it
 * (2026-08-26). (`runnerRoleCanCode` was deleted with the pinned `scout`/`setup` roles
 * it fenced: it answered `true` for every role left.)
 */
export const RunnerRole = {
  Worker: 'worker',
  Universal: 'universal',
} as const;
export const RunnerRoleSchema = z.enum(RunnerRole);
export type RunnerRole = z.infer<typeof RunnerRoleSchema>;

/** The env var by which an operator PINS a box to one role. Home is here, not in
 * `apps/runner`, because the refusal texts that tell a caller about it are
 * rendered in `packages/mcp` and an app is not importable from a package. */
export const RUNNER_MODE_ENV = 'DZ_RUNNER_MODE' as const;

/**
 * The role a box takes when its env pins NONE — what `apps/runner`'s
 * `resolveRunnerMode` returns for an unset or empty {@link RUNNER_MODE_ENV}. An
 * unpinned box is FUNGIBLE (#547): it picks its executor per claimed assignment,
 * which is why the unpinned answer is `universal` and not the narrower `worker`.
 *
 * It lives here so the prose that TELLS a caller about it is built from it rather
 * than restating it. Three MCP strings restated it as `worker` and were wrong:
 * they sent an agent to set `DZ_RUNNER_MODE=universal` on a box that already was,
 * or to re-provision one that needed nothing.
 */
export const UNPINNED_RUNNER_ROLE: RunnerRole = RunnerRole.Universal;

/**
 * What `runners.mode` reads before the box has advertised anything — the COLUMN
 * default, and a placeholder rather than an observation.
 *
 * Enrolment flips a row `pending → online` and writes no `mode` (`enroll`,
 * `@developerz/db`), so a box is online and serving with this value in its row
 * until the presence sweep syncs the role it actually booted into
 * (`updateRunnerMode`, driven from live NATS presence). Like every role it can code, so
 * coding dispatch is never delayed by the gap — but {@link runnerRoleCanConverse} refuses it, so scout capacity reads
 * false for an unpinned box that will shortly advertise {@link UNPINNED_RUNNER_ROLE}.
 * Any refusal keyed on `mode` has to say that, or it accuses a correct box.
 */
export const UNADVERTISED_RUNNER_ROLE: RunnerRole = RunnerRole.Worker;

/**
 * Can a box pinned to this role actually RUN an interactive session — claim a
 * `scout`/`setup`-ORIGIN assignment off the DB queue AND hold its mailbox? Only
 * `universal`: it reads `isInteractiveAssignment` (`apps/runner/src/modes/universal.ts`)
 * and opens the per-assignment mailbox. A pinned `worker` falls through to
 * `routeAutonomous` and would run the CODING adapter on a planning brief.
 *
 * WHAT THIS PREDICATE DOES NOT DO: it answers whether capacity EXISTS, never which
 * box the assigner will pick. Claim order is `class` then priority then age
 * (`packages/db` `claimNextReady`), and this predicate has no say in it.
 *
 * THE GATE THAT USES IT NOW EXISTS, and this paragraph used to say the opposite.
 * For the fleet's whole life the claim path was one-directional — the (since-deleted)
 * `runnerRoleCanCode` fenced pinned `scout`/`setup` boxes out of coding work and NOTHING
 * fenced the inverse — so on a mixed fleet a pinned `worker` claimed interactive sessions and
 * mis-executed them: measured in prod 2026-08-26, 39 of the 52 `origin: 'scout'`
 * tasks that reached a `worker` box FAILED (75%), against 1 of 438 on a `universal`
 * box. That is closed. `claimNextReady` takes `interactiveOriginsAllowed`, the
 * assigner passes THIS predicate's answer for the claiming box's role, and
 * `claimAndOutbox` keeps a post-claim belt (`InteractiveRoleRefused`) for the leak
 * case. The composition lives in ONE place — `runnerRoleCanRunOrigin`
 * (`../value-objects/task-envelope`) — so a caller never has to remember the pair.
 *
 * The two predicates are still SEPARATE, and that is the point: asking only the
 * coding half is how a `worker` box came to claim a planning session.
 */
export function runnerRoleCanConverse(role: RunnerRole): boolean {
  return role === RunnerRole.Universal;
}
